import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FiSave, FiArrowLeft, FiGlobe } from 'react-icons/fi';

const BlogEditor = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [activeTab, setActiveTab] = useState('en'); // 'en' | 'ar'
    const [formData, setFormData] = useState({
        title: '',
        title_ar: '',
        category: 'Cleaning',
        category_ar: '',
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        author: 'Admin',
        excerpt: '',
        excerpt_ar: '',
        content: '',
        content_ar: '',
        image: ''
    });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            setIsLoading(true);
            fetch(`/api/blogs/read.php?id=${id}`)
                .then(res => res.json())
                .then(data => {
                    if (data && data.title) {
                        setFormData(prev => ({ ...prev, ...data }));
                    }
                })
                .catch(err => console.error('Failed to load blog post:', err))
                .finally(() => setIsLoading(false));
        }
    }, [id, isEditMode]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = isEditMode ? '/api/blogs/update.php' : '/api/blogs/create.php';
            const payload = isEditMode ? { ...formData, id } : formData;

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success(isEditMode ? 'Blog updated successfully' : 'Blog created successfully');
                navigate('/admin/blogs');
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('API Error:', response.status, response.statusText, errorData);
                toast.error(`Failed to ${isEditMode ? 'update' : 'create'} blog: ${errorData.message || response.statusText}`);
            }
        } catch (error) {
            console.error('Network/Code Error:', error);
            toast.error('Failed to connect to the server. Check console for details.');
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin/blogs')} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <FiArrowLeft size={24} className="text-gray-500" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800">{isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}</h1>
                </div>

                {/* Language Switcher Tabs */}
                <div className="flex bg-gray-100 p-1 rounded-xl">
                    <button
                        type="button"
                        onClick={() => setActiveTab('en')}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${activeTab === 'en' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        English (EN)
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('ar')}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'ar' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        <FiGlobe size={14} /> العربية (AR)
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-8 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                        <select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                        >
                            <option>Commercial Cleaning</option>
                            <option>Facility Management</option>
                            <option>Building Maintenance</option>
                            <option>Safety & Standards</option>
                            <option>Specialized Services</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                        <input
                            type="url"
                            name="image"
                            value={formData.image}
                            onChange={handleChange}
                            placeholder="https://example.com/image.jpg"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>
                </div>

                {/* Tab: English Content */}
                {activeTab === 'en' && (
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Title (English) *</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt (English)</label>
                            <textarea
                                name="excerpt"
                                value={formData.excerpt}
                                onChange={handleChange}
                                rows="3"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Short summary of the post..."
                            ></textarea>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Content (English - HTML allowed) *</label>
                            <textarea
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                rows="12"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                                placeholder="<p>Write your blog post content here...</p>"
                                required
                            ></textarea>
                        </div>
                    </div>
                )}

                {/* Tab: Arabic Content */}
                {activeTab === 'ar' && (
                    <div className="space-y-6" dir="rtl">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">العنوان بالعربية (Title Arabic - Optional)</label>
                            <input
                                type="text"
                                name="title_ar"
                                value={formData.title_ar || ''}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-sans"
                                placeholder="اكتب عنوان المقال باللغة العربية..."
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">المخاطر/الملخص (Excerpt Arabic)</label>
                            <textarea
                                name="excerpt_ar"
                                value={formData.excerpt_ar || ''}
                                onChange={handleChange}
                                rows="3"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-sans"
                                placeholder="ملخص قصير للمقال بالعربية..."
                            ></textarea>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">المحتوى بالعربية (Content Arabic - HTML)</label>
                            <textarea
                                name="content_ar"
                                value={formData.content_ar || ''}
                                onChange={handleChange}
                                rows="12"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-sans text-sm"
                                placeholder="<p>اكتب محتوى المقال هنا باللغة العربية...</p>"
                            ></textarea>
                        </div>
                    </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                        {activeTab === 'en' ? 'Tip: Switch tabs to add Arabic translations for future blog posts.' : 'ملاحظة: محتوى اللغة العربية اختياري وسيستخدم النسخة الإنجليزية كخيار احتياطي.'}
                    </span>
                    <button
                        type="submit"
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-md"
                    >
                        <FiSave />
                        <span>Publish Post</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BlogEditor;
